import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, Users, WifiOff, Send, ShieldAlert, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import PilotFeedbackWidget from "../components/PilotFeedbackWidget";

/* Spec §18 — Pilot dashboard: single ops view of the four pilot cohorts. */

export default function PilotDashboard() {
  const { user } = useAuth() || {};
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/pilot/overview").then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed"));
  }, [user]);

  if (!user) return <div className="max-w-lg mx-auto p-8 text-center"><Link to="/login">Sign in</Link></div>;
  if (err) return <div className="max-w-lg mx-auto p-8 text-center text-slate-600"><div className="font-serif font-800 text-2xl">Ops-admin only</div><div className="text-sm mt-2">{err}</div></div>;
  if (!data) return <Spinner />;

  const cohortLabels = { individual: "Individual", couple: "Couple", family: "Family / Small", large_group: "Large Group", trial: "Trial" };
  const cohortColors = { individual: "bg-blue-500", couple: "bg-indigo-500", family: "bg-emerald-500", large_group: "bg-amber-500", trial: "bg-slate-400" };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="pilot-dashboard">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-600 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Spec §18 · Pilot Overview</div>
      <h1 className="font-serif font-800 text-4xl text-slate-900 mt-1">Pilot cohorts</h1>

      {/* Cohort bars */}
      <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-3">Members by plan</div>
        {Object.entries(cohortLabels).map(([k, l]) => {
          const n = data.cohorts[k] || 0;
          const total = Object.values(data.cohorts).reduce((a, b) => a + b, 0) || 1;
          return (
            <div key={k} className="mb-2.5 last:mb-0" data-testid={`cohort-${k}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-700 text-slate-800">{l}</span>
                <span className="text-slate-500 font-mono">{n}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${cohortColors[k]}`} style={{ width: `${(n / total) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </section>

      {/* KPI grid */}
      <section className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi Icon={Send} label="Invitations sent" value={data.invitations.total} sub={`${data.invitations.acceptance_rate}% accepted`} tone="blue" testid="kpi-invites" />
        <Kpi Icon={Users} label="Inner Circle members" value={data.inner_circle_members} sub="Across all owners" tone="violet" testid="kpi-circle" />
        <Kpi Icon={WifiOff} label="Offline coverage" value={`${data.offline_readiness.coverage_pct}%`} sub={`${data.offline_readiness.devices_with_offline} / ${data.offline_readiness.devices_total} devices`} tone="green" testid="kpi-offline" />
        <Kpi Icon={ShieldAlert} label="SOS events" value={data.sos_test_events} sub="Test + production" tone="rose" testid="kpi-sos" />
      </section>

      {/* Lifecycle */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-3">Trips by lifecycle</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.lifecycle).map(([k, v]) => (
            <span key={k} className="rounded-full bg-slate-100 text-slate-800 text-xs font-700 px-3 py-1" data-testid={`lifecycle-${k}`}>
              {k}: <b>{v}</b>
            </span>
          ))}
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-blue-50 p-5 flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-blue-600" />
        <div className="text-sm text-blue-900">
          <b>{data.template_applications}</b> template task{data.template_applications === 1 ? "" : "s"} applied across all pilot trips.
        </div>
      </section>

      {/* §20 — Instrumented feedback capture for the 4 cohorts + alumni group */}
      <section className="mt-6" data-testid="pilot-feedback-section">
        <PilotFeedbackWidget inline />
      </section>

      <PilotFeedback data={data} />
    </div>
  );
}

function PilotFeedback({ data }) {
  const [rows, setRows] = useState(null);
  const [digestTo, setDigestTo] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  useEffect(() => {
    import("../lib/api").then(({ default: api }) =>
      api.get("/pilot/feedback").then((r) => setRows(r.data)).catch(() => setRows({ items: [], totals: { up: 0, down: 0 }, by_cohort: {} }))
    );
  }, []);
  const sendDigest = async () => {
    if (!/^\S+@\S+\.\S+$/.test(digestTo)) { return; }
    setDigestBusy(true);
    try {
      const { default: api } = await import("../lib/api");
      await api.post("/pilot/feedback/digest", { to: digestTo, limit: 50 });
      const { toast } = await import("sonner");
      toast.success(`Digest sent to ${digestTo}`);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally { setDigestBusy(false); }
  };
  if (!rows) return null;
  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="pilot-feedback-log">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500">Live feedback log</div>
          <div className="text-sm text-slate-700 mt-0.5">
            <span className="text-emerald-700 font-800">{rows.totals.up} 👍</span> ·{" "}
            <span className="text-rose-700 font-800">{rows.totals.down} 👎</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={digestTo}
            onChange={(e) => setDigestTo(e.target.value)}
            placeholder="ops@example.com"
            data-testid="pilot-digest-email"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500 w-56"
          />
          <button
            onClick={sendDigest}
            disabled={digestBusy || !digestTo}
            data-testid="pilot-digest-send"
            className="inline-flex items-center gap-1.5 bg-fuchsia-600 text-white text-xs font-800 px-3 py-2 hover:bg-fuchsia-700 disabled:opacity-40">
            {digestBusy ? "Sending…" : "Email digest"}
          </button>
        </div>
      </div>
      {rows.items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="pilot-feedback-empty">No feedback yet — participants can leave thumbs + a note from any page.</p>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {rows.items.slice(0, 50).map((i) => (
            <li key={i.id} className="py-2 text-sm flex items-start gap-2" data-testid={`pilot-fb-${i.id}`}>
              <span className="text-base leading-none mt-0.5">{i.rating === "up" ? "👍" : "👎"}</span>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-500">
                  <span className="font-mono">{(i.at || "").slice(0, 16).replace("T", " ")}</span>
                  {i.cohort && <> · <span className="font-800 text-slate-700">{i.cohort}</span></>}
                  {i.page && <> · <span className="font-mono">{i.page}</span></>}
                </div>
                {i.text && <div className="text-slate-800 mt-0.5 whitespace-pre-wrap break-words">{i.text}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Kpi({ Icon, label, value, sub, tone, testid }) {
  const toneColor = { blue: "text-blue-700", violet: "text-violet-700", green: "text-emerald-700", rose: "text-rose-700" }[tone] || "text-slate-700";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-800 uppercase tracking-widest text-slate-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`font-serif font-800 text-3xl mt-1 ${toneColor}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
